import { Menu, Button, Text, Stack, Flex, Box, Timeline, Group, Modal, Loader, Avatar, Tooltip, createStyles } from '@mantine/core';
import {  IconTrash, IconDots, IconCheck, IconCirclePlus, IconPencil, IconClock, IconDownload } from '@tabler/icons';
import { useContext, useState } from 'react';
import IdeaContext from '../IdeaContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { closeAllModals, openConfirmModal, openModal } from '@mantine/modals';
import { deleteIdea, saveIdea, getIdeaActivityLogs } from '../../../Api';
import { ArchiveIcon, IconHistory } from '../../../icons/x-symbol-svgrepo-com';
import { showNotification } from '@mantine/notifications';
import { downloadIdeaToCSV } from '../../../utils/downloadIdeaToCSV';

export function IdeaMenu() {

  const { idea, ideaState, isDeliveryLinkPresent, loadIdea, setIdeaHistoryView, setCardView, allRequests, matches, requestsMatches } = useContext(IdeaContext);
  const auth0 = useAuth0();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);

  const openDeleteRequest = () =>{

    const validated = allRequests.filter((request: any) => request.status === 'validated')

    if(isDeliveryLinkPresent || validated.length > 0){
      openModal({
        modalId: "decline",
        title: <Text fz={20} fw={600} ml={20} mt={40}>Delete idea</Text>,
        centered: true,
        size: 496,
        styles: {
          close: {
            alignSelf: "flex-start",  
            marginTop: 10,
            marginRight: 10,
          },
          header:{
            padding: "10px 20px"
          },
          body: {
            height: 186, 
          },
        },
        children: (
          <Stack p={20}>
            <Box fz={14}>
              <Text>There are requests assigned to this idea.</Text>
              <Text> Please re-assign them before deleting the idea</Text>
            </Box>
            <Flex justify="end" mt="44px">
              <Button
                onClick={() => {
                  closeAllModals();
                }}
                styles={(theme) => ({
                  root: {
                    backgroundColor: "#5B60E3",
                    border: 0,
                    height: 32,
                    "&:hover": {
                      backgroundColor: theme.fn.darken("#5B60E3", 0.05),
                    },
                  },
                })}
                px={8}
                leftIcon={<IconCheck size={14} />}
              >
                OK
              </Button>
            </Flex>
          </Stack>
        ),
      });
    }
    else{
      openConfirmModal({
        title: <Text fz={20} fw={600} ml={20} mt={40}>Delete idea</Text>,
        size: 496,
        centered: true,
        children: (
          <Text size="sm" mt={15} mb={30}>Are you sure you want to delete this idea?</Text>
        ),
        styles: {
          close: {
            alignSelf: "flex-start",  
            marginTop: 10,
            marginRight: 10,
          },
          header:{
            padding: "10px 20px"
          },
          body: {
            height: 120, 
            padding: 40,
          },
          
        },
        labels: { confirm: "Delete idea", cancel: "No, keep it" },
        confirmProps: {
          color: "red",
          px: 8,
          styles: {
            label: {
              fontSize: 12, 
              fontWeight: 500
            },
          },
          leftIcon: <IconTrash size={14} />,
        },
        cancelProps: {
          leftIcon: <IconCheck size={16} />,
          px: 8,
          styles: {
            label: {
              fontSize: 12, 
              fontWeight: 500
            },
          },
        },
        onCancel: () => console.log("Cancel"),
        onConfirm: () => {
          deleteIdea(idea?._id, auth0)
            .then((response) => {
              navigate("/ideas");
            })
            .catch((e: any) => console.log(e));
        },
      });
    }

  }

  const handleArchiveIdea = () => {
    const isArchived = ideaState === "archived";
    const request = {
      id: idea?._id,
      state: isArchived ? "active" : "archived",
    };
    saveIdea(request, auth0)
      .then((res) => {
        loadIdea();
        showNotification({
          title: isArchived ? "Idea unarchived" : "Idea archived",
          color: "teal",
          message: <Text
            onClick={() => {
              request.state = isArchived ? "archived" : "active";
              saveIdea(request, auth0)
                .then(() => {
                  loadIdea();
                  showNotification({
                    id: 'idea-archive',
                    title: isArchived ? "Idea archived" : "Idea unarchived",
                    message: `Idea has been restored to ${ideaState}`,
                    color: "teal",
                    autoClose: 3000,
                    icon: <IconCheck size="1.1rem" />,
                  });
                })
                .catch((e) => console.error("Undo failed", e));
            }}
            style={{
              color: "#228be6",
              cursor: "pointer",
              fontWeight: 500,
              display: "inline-block",
              marginTop: 8,
            }}
          >
            Undo
          </Text>,
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200
        });
      })
      .catch((e) => console.log(e));
  }

  const ideaHistory = async () => {
    setIdeaHistoryView(true);
    setCardView(false)
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
    case 'idea_created':
      return { icon: IconCirclePlus, color: 'green' };
    case 'idea_updated':
      return { icon: IconPencil, color: 'blue' };
    default:
      return { icon: IconPencil, color: 'gray' };
    }
  };

  const hasOrigin: boolean = idea?.origin === 'jira' || idea?.origin === 'clickup'

  // Add this constant for allowed fields
  const ALLOWED_FIELDS = ['ownerName', 'componentId', 'status', 'state'];

  // Add this helper function to get readable field names
  const getReadableFieldName = (field: string) => {
    const fieldMap: { [key: string]: string } = {
      ownerName: 'Owner',
      componentId: 'Product Area',
      status: 'Status',
      state: 'State'
    };
    return fieldMap[field] || field;
  };

  return (
    <>


      <Menu width={200} opened={opened} onChange={setOpened}>
        <Menu.Target>
          
            
          <Button styles={(theme) => ({
            root: {
              height: "24px",
              width: "24px",
              backgroundColor: opened ?'rgba(92, 92, 235, 0.4)' : 'rgba(92, 92, 235, 0.1)',
              border: 0,
              fontWeight: 400,
              padding: 0,
              borderRadius:"4px",
              "&:hover": {
                backgroundColor: opened ?'rgba(92, 92, 235, 0.4)' : 'rgba(92, 92, 235, 0.1)',
              },
            },

            leftIcon: {
              marginRight: 15,
            },
          })}>
            <IconDots size={18} color='black'/>
          </Button>
            
            
            
          {/* </Stack> */}
        </Menu.Target>
        <Menu.Dropdown>
          {ideaState === "active" || ideaState === "archived"}
          <Menu.Item
            onClick={ideaHistory}
            color={hasOrigin ? "black" : "black"}
            icon={<IconHistory/>}
          >
            Idea history
          </Menu.Item>
          <Menu.Item
            onClick={handleArchiveIdea}
            color={hasOrigin ? "black" : "black"}
            icon={<ArchiveIcon />}
          >
            {ideaState === "archived" ? "Unarchive idea" : "Archive idea"}
          </Menu.Item> 
          <Menu.Item
            onClick={() => downloadIdeaToCSV({ idea, allRequests, matches, requestsMatches })}
            color={hasOrigin ? "black" : "black"}
            icon={<IconDownload size={14} />}
          >
            Download idea
          </Menu.Item>
          <Menu.Item
            onClick={openDeleteRequest}
            color={hasOrigin ? "#808080" : "red"}
            icon={<IconTrash size={14} />}
          >
                Delete idea
          </Menu.Item> 
          
        </Menu.Dropdown>
      </Menu>
    </>
  );
}